import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Contact } from '../../../../constants';
import { Button, Link, LoadingDots, Text } from '../../../ui';
import { FlexContainer } from '../../../ui/Container';
import { WizardFormData } from '../types';

const TermsContainer = styled.div`
  background-color: ${(p) => p.theme.colors.grey[5]};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.sm};
  max-width: 500px;
  width: 100%;
`;

const TermsSummary = styled.div`
  margin-bottom: ${(p) => p.theme.spacing.sm};
`;

const TermsItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  font-size: ${(p) => p.theme.font.size.xs};
  color: ${(p) => p.theme.colors.general.text};

  &::before {
    content: '•';
    color: ${(p) => p.theme.colors.general.primary};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: ${(p) => p.theme.spacing.xs};
  background-color: ${(p) => p.theme.colors.general.white};
  border: 1px solid ${(p) => p.theme.colors.border.pya};
  border-radius: ${(p) => p.theme.radius.sm};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${(p) => p.theme.colors.general.primary};
  }

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: ${(p) => p.theme.colors.general.primary};
  }
`;

const termsSummaryItems = [
  'Your personal data will be processed in accordance with our Privacy Policy',
  'You agree to receive communications about your membership and PYA services',
  'Membership fees are non-refundable except as required by law',
  'You can cancel your membership at any time through your account settings',
];

interface TermsStepProps {
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  isNonMember: boolean;
}

const TermsStep: FC<TermsStepProps> = ({ onSubmit, onBack, isSubmitting, isNonMember }) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<WizardFormData>();

  const termsAccepted = watch('termsAndConditionsAccepted');

  return (
    <FlexContainer flexDirection="column" gap="md" align="center">
      <FlexContainer flexDirection="column" align="center" gap="xxs">
        <Text weight="semibold" size="lg" style={{ textAlign: 'center' }}>
          Terms and Conditions
        </Text>
        <Text color="textSecondary" size="sm" style={{ textAlign: 'center' }}>
          Please review and accept our terms to continue
        </Text>
      </FlexContainer>

      <TermsContainer>
        <TermsSummary>
          <Text weight="medium" size="sm" style={{ marginBottom: '8px' }}>
            Key points:
          </Text>
          {termsSummaryItems.map((item, index) => (
            <TermsItem key={index}>{item}</TermsItem>
          ))}
        </TermsSummary>

        <Controller
          name="termsAndConditionsAccepted"
          control={control}
          render={({ field: { onChange, value } }) => (
            <CheckboxWrapper>
              <input
                type="checkbox"
                checked={value ?? false}
                onChange={(e) => onChange(e.target.checked)}
                disabled={isSubmitting}
              />
              <Text size="xs" color="black">
                I have read and agree to the{' '}
                <Link href={Contact.TERMS_CONDITIONS} target="_blank" size="xs">
                  Terms and Conditions
                </Link>
              </Text>
            </CheckboxWrapper>
          )}
        />

        {errors.termsAndConditionsAccepted && (
          <Text color="error" size="xs" style={{ marginTop: '8px' }}>
            {errors.termsAndConditionsAccepted.message}
          </Text>
        )}
      </TermsContainer>

      <FlexContainer gap="sm" justify="center" style={{ marginTop: '8px' }}>
        <Button type="button" color="transparent" textColor="textSecondary" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          type="button"
          color="primary"
          onClick={onSubmit}
          disabled={!termsAccepted || isSubmitting}
          style={{ minWidth: '150px' }}
        >
          {isSubmitting ? <LoadingDots color="white" /> : isNonMember ? 'Create Account' : 'Continue to Payment'}
        </Button>
      </FlexContainer>
    </FlexContainer>
  );
};

export default TermsStep;
